import { useEffect, useState } from "react";
import { getBranches, addBranch, deleteBranch, updateBranch, deactivateBranch } from "../services/branchService";
import { TextField, Button, Snackbar, Alert, MenuItem } from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { formatBranchLabel } from "../utils/formatters";
import ActionButtons from "../components/common/ActionButtons";
import { getCurrentUser } from "../utils/currentUser";

function Branch() {
    const [branches, setBranches] = useState([]);
    const [search, setSearch] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortField, setSortField] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [showInactive, setShowInactive] = useState(false);

    const [form, setForm] = useState({
        id: 0,
        branchCode: "",
        branchName: "",
        address: "",
        mobileNo: "",
        managerName: "",
        status: "Active"
    });

    const [isEdit, setIsEdit] = useState(false);
    // ✅ ADD HERE
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });

    const user = getCurrentUser();

    const [page, setPage] = useState(0);

    const role =
        user?.role?.toUpperCase() || "";

    const canEdit =
        role === "ADMIN" ||
        role === "MANAGER";

    const canDelete =
        role === "ADMIN";

    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!loaded) {
            loadData();
            setLoaded(true);
        }
    }, [loaded]);

    // ✅ ADD THIS BELOW
    useEffect(() => {
        setPage(0);
    }, [search, rowsPerPage, showInactive]);

    useEffect(() => {
        if (!isEdit && branches.length > 0) {
            const newCode = generateBranchCode();

            setForm(prev => {
                if (prev.branchCode === newCode) return prev;

                return {
                    ...prev,
                    branchCode: newCode
                };
            });
        }
    }, [branches, isEdit]);

    const loadData = () => {
        getBranches().then(res => {
            console.log("API DATA:", res.data);
            setBranches(res.data);
        });
    };

    const generateBranchCode = () => {
        if (!branches.length) return "BR001";

        const numbers = branches
            .map(b => b.branchCode)
            .filter(code => /^BR\d+$/.test(code))
            .map(code => parseInt(code.replace("BR", ""), 10));

        const max = Math.max(...numbers, 0);

        return `BR${(max + 1).toString().padStart(3, "0")}`;
    };
    // ✅ ADD THIS HERE (payload builder)
    const buildBranchPayload = (b, status) => ({
        id: b.id,
        //branchCode: b.branchCode,
        branchName: b.branchName,
        address: b.address,
        mobileNo: b.mobileNo,
        managerName: b.managerName,
        status
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const isDuplicate = () => {
        const code = (form.branchCode || "").toLowerCase().trim();
        const name = (form.branchName || "").toLowerCase().trim();

        return branches.some(
            b =>
                b.id !== form.id &&
                (
                    (b.branchCode || "").toLowerCase().trim() === code ||
                    (b.branchName || "").toLowerCase().trim() === name
                )
        );
    };

    const handleSave = () => {

        console.log("SAVE CLICKED");

        // 🚨 CRITICAL FIX
        if (isEdit && (!form.id || form.id === 0)) {
            setSnackbar({
                open: true,
                message: "Invalid Branch selected for update",
                severity: "error"
            });
            return;
        }

        if (!form.branchName || form.branchName.trim() === "") {
            setSnackbar({
                open: true,
                message: "Branch Name is required",
                severity: "warning"
            });
            return;
        }

        setIsSaving(true);


         console.log("FORM DATA:", form);

        const cleanedForm = {
            ...form,
            branchName: (form.branchName || "").trim(),
            branchCode: (form.branchCode || "").toUpperCase()
        };

        // 🔴 ADD DUPLICATE CHECK HERE
        const isDuplicateEntry = branches.some(
            b =>
                b.id !== cleanedForm.id &&
                (
                    (b.branchCode || "").toLowerCase().trim() === cleanedForm.branchCode.toLowerCase().trim() ||
                    (b.branchName || "").toLowerCase().trim() === cleanedForm.branchName.toLowerCase().trim()
                )
        );

        if (isDuplicateEntry) {
            setSnackbar({
                open: true,
                message: "Duplicate Branch Code or Name",
                severity: "warning"
            });
            setIsSaving(false);
            return;
        }

        const payload = {
            id: cleanedForm.id,
            branchName: cleanedForm.branchName,
            address: cleanedForm.address,
            mobileNo: cleanedForm.mobileNo,
            managerName: cleanedForm.managerName,
            status: cleanedForm.status
        };

         if (isEdit) {
             updateBranch(cleanedForm.id, payload)
                 .then((res) => {

                     const updated = res.data?.data || res.data;

                     const label = formatBranchLabel(updated);

                     setSnackbar({
                         open: true,
                         message: "Updated Successfully",
                         severity: "success"
                     });
                     setForm({
                         id: 0,
                         branchCode: "",
                         branchName: "",
                         address: "",
                         mobileNo: "",
                         managerName: "",
                         status: "Active"
                     });
                     //setForm({
                     //    id: updated.id,
                     //    branchCode: updated.branchCode || "",
                     //    branchName: updated.branchName || "",
                     //    address: updated.address || "",
                     //    mobileNo: updated.mobileNo || "",
                     //    managerName: updated.managerName || "",
                     //    status: updated.status || "Active"
                     //});

                     setIsEdit(false);

                     loadData();
                 })
                 .catch(err => {
                     console.log("UPDATE ERROR:", err?.response?.data);

                     const msg =
                         err?.response?.data?.title ||
                         err?.response?.data?.message ||
                         (err?.response?.data?.errors
                             ? Object.values(err.response.data.errors).flat().join(", ")
                             : null) ||
                         "Update failed";

                     setSnackbar({
                         open: true,
                         message: msg,
                         severity: "error"
                     });

                     setIsSaving(false); // 🔥 EXTRA SAFETY
                 })
                 .finally(() => {
                     setIsSaving(false);
                 });
        }
         else {
             addBranch(payload)
                 .then((res) => {

                     const created = res.data;

                     const label = formatBranchLabel(created);

                     setSnackbar({
                         open: true,
                         message: "Saved Successfully",
                         severity: "success"
                     });

                     // 🔥 reset form after save
                     setForm({
                         id: 0,
                         branchCode: "",
                         branchName: "",
                         address: "",
                         mobileNo: "",
                         managerName: "",
                         status: "Active"
                     });

                     loadData();
                 })
                 .catch(err => {
                     console.log("CREATE ERROR:", err?.response?.data);

                     const msg =
                         err?.response?.data?.title ||
                         err?.response?.data?.message ||
                         "Save failed";

                     setSnackbar({
                         open: true,
                         message: msg,
                         severity: "error"
                     });

                     setIsSaving(false);
                 })
                 .finally(() => {
                     setIsSaving(false);
                 });
         }
    };

    const handleEdit = (branch) => {
        if (!branch) return;   // ✅ safety check

        setForm({
            id: branch.id,
            branchCode: branch.branchCode || "",
            branchName: branch.branchName || "",
            address: branch.address || "",
            managerName: branch.managerName || "",
            mobileNo: branch.mobileNo || "",
            status: branch.status || "Active"
        });

        setIsEdit(true);
    };

    const handleRestore = async (b) => {
        if (!b) return;                    // ✅ safety
        if (b.status === "Active") return; // ✅ avoid duplicate restore

        try {
            const updated = buildBranchPayload(b, "Active");

            await updateBranch(b.id, updated);

            const label = formatBranchLabel(b);

            loadData();
        } catch (err) {
            console.error("Restore failed:", err);
        }
    };

    const handleDelete = async (b) => {
        if (!b) return;   // ✅ ADD THIS

        if (b?.status === "Inactive") return;

        if (!window.confirm("Deactivate this branch?")) return;

        try {
            const updated = buildBranchPayload(b, "Inactive");

            await updateBranch(b.id, updated);

            const label = formatBranchLabel(b);

            loadData();
        } catch (err) {
            console.error("Delete failed:", err);
        }
    };

    const handlePermanentDelete = async (b) => {
        if (!b) return;
        if (!window.confirm(`Delete "${b.branchName}" permanently?`)) return;

        try {
            await deleteBranch(b.id);

            const label = formatBranchLabel(b);

            loadData();
        } catch (err) {
            console.error("Permanent delete failed:", err);
        }
    };

    const resetForm = () => {
        setForm({
            id: 0,
            branchCode: "", // auto-filled by useEffect
            branchName: "",
            address: "",
            mobileNo: "",
            managerName: "",
            status: "Active"
        });

        setIsEdit(false);
        setIsSaving(false);   // ✅ optional safety
    };

    let filtered = branches.filter(b => {
        if (!showInactive && b.status === "Inactive") return false;

        const searchText = search.toLowerCase();

        return (
            (b.branchCode || "").toLowerCase().includes(searchText) ||
            (b.branchName || "").toLowerCase().includes(searchText) ||
            (b.managerName || "").toLowerCase().includes(searchText)
        );
    });

    const sorted = [...filtered];

    if (sortField) {
        sorted.sort((a, b) => {
            let valA = a[sortField] || "";
            let valB = b[sortField] || "";

            // Special handling for status
            if (sortField === "status") {
                const order = { Active: 1, Inactive: 2 };
                valA = order[valA] || 3;
                valB = order[valB] || 3;
            } else {
                valA = valA.toLowerCase();
                valB = valB.toLowerCase();
            }

            if (valA < valB) return sortOrder === "asc" ? -1 : 1;
            if (valA > valB) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
    }

    // 3️⃣ LAST → pagination
    const paginated = sorted.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const totalPages = Math.ceil(sorted.length / rowsPerPage);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const exportToExcel = () => {

        // ✅ empty check
        if (!sorted || sorted.length === 0) {
            setSnackbar({
                open: true,
                message: "No data to export",
                severity: "warning"
            });
            return;
        }

        const exportData = sorted.map(b => ({
            Code: b.branchCode,
            Name: b.branchName,
            Address: b.address,
            Manager: b.managerName,
            Mobile: b.mobileNo,
            Status: b.status
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(wb, ws, "Branches");

        const excelBuffer = XLSX.write(wb, {
            bookType: "xlsx",
            type: "array"
        });

        const blob = new Blob([excelBuffer], {
            type: "application/octet-stream"
        });

        saveAs(blob, "Branches.xlsx");

        // ✅ optional audit (recommended)
    };
    return (
        <div style={styles.container}>

            <div style={styles.header}>
                <h2>Branch Master</h2>
            </div>

            <div style={styles.card}>
                <div style={styles.formContainer}>

                    {/* ROW 1 */}
                    <div style={styles.row}>
                        <TextField
                            label="Branch Code"
                            name="branchCode"
                            value={form.branchCode}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            sx={styles.inputStyle}
                            InputProps={{ readOnly: true }}
                        />

                        <TextField
                            label="Branch Name"
                            name="branchName"
                            value={form.branchName}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            sx={styles.inputStyle}
                        />
                    </div>

                    {/* ROW 2 */}
                    <div style={{ display: "flex", gap: "10px" }}>

                        {/* LEFT SIDE - ADDRESS */}
                        <div style={{ flex: 1, display: "flex" }}>
                            <TextField
                                label="Address"
                                name="address"
                                value={form.address}
                                onChange={handleChange}
                                fullWidth
                                multiline
                                minRows={3}   // 🔥 adjust height
                                sx={{
                                    ...styles.inputStyle,
                                    flex: 1,
                                    "& .MuiInputBase-root": {
                                        height: "100%",          // ✅ stretch full height
                                        alignItems: "flex-start" // ✅ text starts from top
                                    }
                                }}
                            />
                        </div>

                        {/* RIGHT SIDE */}
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            flex: 1
                        }}>

                            <TextField
                                label="Manager Name"
                                name="managerName"
                                value={form.managerName}
                                onChange={handleChange}
                                fullWidth
                                size="small"   
                                sx={styles.inputStyle}
                            />

                            <TextField
                                label="Mobile No"
                                name="mobileNo"
                                value={form.mobileNo}
                                onChange={handleChange}
                                fullWidth
                                size="small"   
                                sx={styles.inputStyle}
                            />

                            <TextField
                                select
                                label="Status"
                                name="status"
                                value={form.status}
                                onChange={handleChange}
                                fullWidth
                                size="small"   
                                sx={styles.inputStyle}
                            >
                                <MenuItem value="Active" sx={{ fontFamily: "Verdana", fontSize: "12px" }}>
                                    Active
                                </MenuItem>

                                <MenuItem value="Inactive" sx={{ fontFamily: "Verdana", fontSize: "12px" }}>
                                    Inactive
                                </MenuItem>                            </TextField>

                        </div>

                    </div>

                </div>
                <div style={styles.buttonRow}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{
                            height: "28px",
                            minHeight: "28px",
                            fontSize: "12px",
                            fontFamily: "Verdana",
                            padding: "0 12px",
                            textTransform: "none !important"
                        }}
                    >
                        {isEdit ? "Update" : "Save"}
                    </Button>

                    {isEdit && (
                        <Button
                            variant="contained"
                            onClick={resetForm}
                            sx={{
                                height: "28px",
                                minHeight: "28px",
                                fontSize: "12px",
                                fontFamily: "Verdana",
                                padding: "0 12px",
                                textTransform: "none !important",
                                marginLeft: "10px",
                                background: "#2f3542",   // 🔵 dark color (or use same as update if you want)
                                color: "#fff",
                                "&:hover": {
                                    background: "#1e272e"
                                }
                            }}
                        >
                            Cancel
                        </Button>
                    )}
                </div>
            </div>

            <div style={styles.card}>

                <div style={{
                    display: "flex",
                    justifyContent: "flex-end",   // ✅ THIS FIXES ALIGNMENT
                    alignItems: "center",
                    gap: "10px",
                    width: "100%"
                }}>
                    {/* ✅ Show Inactive Checkbox */}
                    <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "5px" }}>
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                        />
                        Show Inactive
                    </label>

                    <TextField
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        sx={{
                            width: "220px",

                            "& .MuiOutlinedInput-root": {
                                height: "28px",
                                fontFamily: "Verdana"
                            },

                            "& .MuiOutlinedInput-input": {
                                padding: "4px 10px",
                                fontSize: "12px",
                                fontFamily: "Verdana"
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: search && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch("")}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={exportToExcel}
                        sx={{
                            height: "28px",
                            minHeight: "28px",
                            fontSize: "12px",
                            fontFamily: "Verdana",
                            padding: "0 12px",
                            background: "#3742fa"
                        }}
                    >
                        Export
                    </Button>

                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <span style={{ fontSize: "12px" }}>Rows</span>
                        <select
                            value={rowsPerPage}
                            onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
                            style={{
                                height: "28px",
                                fontSize: "12px",
                                fontFamily: "Verdana"
                            }}
                        >
                            {[5, 10, 20, 30, 50, 100].map(n => (
                                <option key={n}>{n}</option>
                            ))}
                        </select>
                    </div>

                </div>
                <div style={{ ...styles.tableWrapper, marginTop: "10px" }}>
                    <table style={styles.table}>
                        <thead style={styles.tableHeader}>
                            <tr>
                                <th
                                    style={{ ...styles.th, width: "140px", textAlign: "center", cursor: "pointer" }}
                                    onClick={() => handleSort("branchCode")}
                                >
                                    Code ↕
                                </th>
                                <th
                                    style={{ ...styles.th, width: "200px", cursor: "pointer" }}
                                    onClick={() => handleSort("branchName")}
                                >
                                    Name ↕
                                </th>
                                <th style={{ ...styles.th, width: "200px" }}>Address</th>
                                <th
                                    style={{ ...styles.th, width: "180px", cursor: "pointer" }}
                                    onClick={() => handleSort("managerName")}
                                >
                                    Manager ↕
                                </th>
                                <th
                                    style={{ ...styles.th, width: "150px", textAlign: "center", cursor: "pointer" }}
                                    onClick={() => handleSort("mobileNo")}
                                >
                                    Mobile ↕
                                </th>

                                <th
                                    style={{ ...styles.th, width: "120px", textAlign: "center", cursor: "pointer" }}
                                    onClick={() => handleSort("status")}
                                >
                                    Status ↕
                                </th>
                                <th style={{ ...styles.th, width: "100px", textAlign: "center" }}>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {paginated.map((b) => (
                                <tr key={b.id}>
                                    <td style={{ ...styles.td, textAlign: "center" }}>{b.branchCode}</td>
                                    <td style={styles.td}>{b.branchName}</td>
                                    <td style={styles.td}>{b.address}</td>
                                    <td style={styles.td}>{b.managerName}</td>
                                    <td style={{ ...styles.td, textAlign: "center" }}>{b.mobileNo}</td>

                                    <td style={{ ...styles.td, textAlign: "center" }}>
                                        <span
                                            style={{
                                                color: (b.status || "Active") === "Active" ? "green" : "red",
                                                fontWeight: "bold"
                                            }}
                                        >
                                            {b.status || "Active"}
                                        </span>
                                    </td>

                                    {/* ✅ ACTIONS COLUMN (REPLACED) */}
                                    <td style={{ ...styles.td, textAlign: "center" }}>
                                        <ActionButtons
                                            row={b}
                                            status={b.status}
                                            onEdit={handleEdit}
                                            onDelete={handleDelete}
                                            onRestore={handleRestore}
                                            onPermanentDelete={handlePermanentDelete}
                                            canEdit={canEdit}
                                            canDelete={canDelete}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "10px",
                        marginTop: "10px"
                    }}>
                        <button
                            onClick={() => setPage(prev => Math.max(prev - 1, 0))}
                            disabled={page === 0}
                        >
                            Prev
                        </button>

                        <span>
                            Page {page + 1} of {totalPages}
                        </span>

                        <button
                            onClick={() => setPage(prev => prev + 1)}
                            disabled={page + 1 >= totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>

            </div>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

const styles = {


    container: {
        padding: "5px",
        background: "#f1f2f6",
        fontFamily: "Verdana",
        fontSize: "12px"
    },

    header: {
        background: "#2f3542",
        color: "#fff",
        padding: "6px",
        borderRadius: "6px",
        marginBottom: "10px"
    },

    card: {
        background: "#fff",
        padding: "15px",
        borderRadius: "15px",
        marginBottom: "10px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        width: "100%",
        boxSizing: "border-box"
    },

    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "10px"
    },

    buttonRow: { marginTop: "10px" },

    error: {
        color: "red",
        marginBottom: "10px"
    },

    gridHeader: {
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "10px"
    },

    searchContainer: {
        display: "flex",
        gap: "8px",
        alignItems: "center"
    },

    search: {
        padding: "4px 8px",
        height: "20px",
        border: "1px solid #ccc",
        borderRadius: "4px"
    },

    exportBtn: {
        background: "#3742fa",
        color: "#fff",
        border: "none",
        padding: "5px 10px",
        borderRadius: "4px",
        cursor: "pointer"
    },

    tableWrapper: {
        width: "100%",
        overflowX: "auto"
    },

    table: {
        width: "100%",
        borderCollapse: "collapse",
        minWidth: "700px"
    },

    tableHeader: {
        background: "#57606f",
        color: "#fff"
    },

    th: {
        border: "1px solid #ccc",
        padding: "8px",
        fontsize: "12px",
        fontweight: "600"
    },

    td: {
        border: "1px solid #ddd",
        padding: "8px",
        fontsize: "12px"
    },

    editBtn: {
        background: "#2ed573",
        border: "none",
        padding: "5px",
        marginRight: "5px",
        borderRadius: "4px",
        cursor: "pointer"
    },

    deleteBtn: {
        background: "#ff4757",
        border: "none",
        padding: "5px",
        borderRadius: "4px",
        cursor: "pointer"
    },

    formContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "10px"
    },

    row: {
        display: "flex",
        gap: "10px"
    },

    rightColumn: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        flex: 1
    },

    inputStyle: {
        "& .MuiOutlinedInput-root": {
            height: "34px"
        },

        "& .MuiInputBase-input": {
            fontFamily: "Verdana",
            fontSize: "12px",
            padding: "8px 10px"
        },

        "& .MuiInputLabel-root": {
            fontFamily: "Verdana",
            fontSize: "12px"
        },

        // 🔥 THIS IS THE MISSING PIECE (VERY IMPORTANT)
        "& .MuiSelect-select": {
            fontFamily: "Verdana",
            fontSize: "12px",
            padding: "8px 10px !important",
            display: "flex",
            alignItems: "center"
        }
    },
};

export default Branch;